import { useState, useEffect, useCallback } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import api from "../../services/admin_api_global";
import "../payments/paymentDashboard.css";

// ── Helpers ───────────────────────────────────────────────────
const fmtAmt = (v) =>
  v != null ? `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—";

const fmtMonth = (m) => {
  const s = String(m);
  if (!s.includes("-")) return s;
  const [y, mo] = s.split("-");
  return new Date(y, mo - 1).toLocaleDateString("en-IN", { month: "short", year: "numeric" });
};

const todayStr    = () => new Date().toISOString().slice(0, 10);
const monthStart  = () => new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

const PIE_COLORS = ["#019095", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#10b981", "#f97316"];

// ── Stat Card ─────────────────────────────────────────────────
const StatCard = ({ label, value, sub, accent }) => (
  <div className="pd-stat-card" style={{ borderLeftColor: accent }}>
    <div className="pd-stat-label">{label}</div>
    <div className="pd-stat-value" style={{ color: accent }}>{value}</div>
    {sub && <div className="pd-stat-sub">{sub}</div>}
  </div>
);

// ── Section wrapper ───────────────────────────────────────────
const Section = ({ title, loading, error, children }) => (
  <div style={{ background: "#fff", borderRadius: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.07)", padding: "1.25rem 1.5rem", marginBottom: "1.25rem" }}>
    {title && <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#374151", marginBottom: "1rem" }}>{title}</div>}
    {loading ? <div className="pd-loading">Loading…</div>
      : error   ? <div style={{ color: "#dc2626", fontSize: "0.85rem" }}>{error}</div>
      : children}
  </div>
);

// ── Bar chart tooltip ─────────────────────────────────────────
const BarTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px", fontSize: "0.8rem", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.name} style={{ color: p.color, marginBottom: 2 }}>{p.name}: {fmtAmt(p.value)}</div>
      ))}
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────
const PnLDashboard = () => {
  const [overview,  setOverview]  = useState(null);
  const [monthly,   setMonthly]   = useState([]);
  const [rangeData, setRangeData] = useState(null);
  const [breakdown, setBreakdown] = useState([]);

  const [ovLoad, setOvLoad] = useState(true);
  const [moLoad, setMoLoad] = useState(true);
  const [rnLoad, setRnLoad] = useState(false);

  const [ovErr, setOvErr] = useState("");
  const [moErr, setMoErr] = useState("");
  const [rnErr, setRnErr] = useState("");

  const [from, setFrom] = useState(monthStart());
  const [to,   setTo]   = useState(todayStr());

  useEffect(() => {
    api.get("/admin/analytics/pnl/overview")
      .then((r) => setOverview(r.data))
      .catch(() => setOvErr("Failed to load overview."))
      .finally(() => setOvLoad(false));
  }, []);

  useEffect(() => {
    api.get("/admin/analytics/pnl/monthly")
      .then((r) => setMonthly(Array.isArray(r.data) ? r.data : []))
      .catch(() => setMoErr("Failed to load monthly trend."))
      .finally(() => setMoLoad(false));
  }, []);

  const loadRange = useCallback(() => {
    if (!from || !to) return;
    setRnLoad(true); setRnErr("");
    Promise.all([
      api.get("/admin/analytics/pnl/range",             { params: { from, to } }),
      api.get("/admin/analytics/pnl/expenses/breakdown", { params: { from, to } }),
    ])
      .then(([r1, r2]) => {
        setRangeData(r1.data);
        setBreakdown(Array.isArray(r2.data) ? r2.data : []);
      })
      .catch(() => setRnErr("Failed to load range data."))
      .finally(() => setRnLoad(false));
  }, [from, to]);

  useEffect(() => { loadRange(); }, [loadRange]);

  const chartData = monthly.map((m) => ({
    name:         fmtMonth(m.month),
    Income:       m.income,
    Expenses:     m.expenses,
    "Net Profit": m.netProfit,
  }));

  return (
    <div className="pd-page">
      <div className="pd-header">
        <h2 className="pd-title">P&amp;L Analytics</h2>
      </div>

      <div style={{ padding: "1rem 1.5rem", overflowY: "auto", flex: 1 }}>

        {/* ── Overview ── */}
        <Section title="Overview" loading={ovLoad} error={ovErr}>
          {overview && (<>
            <div className="pd-summary-strip" style={{ padding: 0, marginBottom: "1rem" }}>
              <StatCard label="Total Income"   value={fmtAmt(overview.totalIncome)}           accent="#16a34a" />
              <StatCard label="Total Expenses" value={fmtAmt(overview.totalExpenses)}          accent="#dc2626" />
              <StatCard label="Net Profit"     value={fmtAmt(overview.netProfit)}
                accent={overview.netProfit < 0 ? "#dc2626" : "#1d4ed8"}
                sub={overview.netProfit < 0 ? "⚠ Loss" : null} />
              <StatCard label="Profit Margin"  value={`${overview.profitMarginPercent ?? 0}%`} accent="#6d28d9" />
            </div>
            <div style={{ fontWeight: 600, fontSize: "0.72rem", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "0.5rem" }}>Income Breakdown</div>
            <div className="pd-summary-strip" style={{ padding: 0 }}>
              <StatCard label="Subscriptions" value={fmtAmt(overview.incomeBreakdown?.subscriptions)} accent="#019095" />
              <StatCard label="Events"        value={fmtAmt(overview.incomeBreakdown?.events)}        accent="#019095" />
              <StatCard label="Courses"       value={fmtAmt(overview.incomeBreakdown?.courses)}       accent="#019095" />
            </div>
          </>)}
        </Section>

        {/* ── Monthly Trend ── */}
        <Section title="Monthly Trend" loading={moLoad} error={moErr}>
          {chartData.length === 0
            ? <div className="pd-empty">No monthly data available.</div>
            : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6b7280" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<BarTip />} />
                  <Legend wrapperStyle={{ fontSize: "0.8rem" }} />
                  <Bar dataKey="Income"     fill="#16a34a" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Expenses"   fill="#ef4444" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Net Profit" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
        </Section>

        {/* ── Date Range ── */}
        <Section title="Date Range P&L" loading={false} error="">
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "1.25rem" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "#374151" }}>From</label>
              <input type="date" className="pd-input pd-input-date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "#374151" }}>To</label>
              <input type="date" className="pd-input pd-input-date" value={to} min={from} max={todayStr()} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>

          {rnLoad ? <div className="pd-loading">Loading…</div>
            : rnErr ? <div style={{ color: "#dc2626", fontSize: "0.85rem" }}>{rnErr}</div>
            : rangeData && (<>
              <div className="pd-summary-strip" style={{ padding: 0, marginBottom: "1.5rem" }}>
                <StatCard label="Income"        value={fmtAmt(rangeData.totalIncome)}           accent="#16a34a" />
                <StatCard label="Expenses"      value={fmtAmt(rangeData.totalExpenses)}          accent="#dc2626" />
                <StatCard label="Net Profit"    value={fmtAmt(rangeData.netProfit)}
                  accent={rangeData.netProfit < 0 ? "#dc2626" : "#1d4ed8"}
                  sub={rangeData.netProfit < 0 ? "⚠ Loss" : null} />
                <StatCard label="Profit Margin" value={`${rangeData.profitMarginPercent ?? 0}%`} accent="#6d28d9" />
              </div>

              {breakdown.length > 0 && (<>
                <div style={{ fontWeight: 600, fontSize: "0.72rem", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "0.75rem" }}>Expense Breakdown</div>
                <div style={{ display: "flex", gap: "2rem", alignItems: "center", flexWrap: "wrap" }}>
                  <ResponsiveContainer width={240} height={220}>
                    <PieChart>
                      <Pie data={breakdown} dataKey="total" nameKey="category" cx="50%" cy="50%" outerRadius={90} innerRadius={52}>
                        {breakdown.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => fmtAmt(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    {breakdown.map((b, i) => (
                      <div key={b.category} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.82rem" }}>
                        <div style={{ width: 10, height: 10, borderRadius: "50%", background: PIE_COLORS[i % PIE_COLORS.length], flexShrink: 0 }} />
                        <span style={{ fontWeight: 600, color: "#374151" }}>{b.category}</span>
                        <span style={{ color: "#6b7280", marginLeft: "auto", paddingLeft: 24 }}>{fmtAmt(b.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>)}
            </>)}
        </Section>

      </div>
    </div>
  );
};

export default PnLDashboard;
